/** MeetingMatterBinding — 会议详情里的「关联事项」区块。
 *
 * 关联与解除关联都改变这场会议的可见范围，两个方向都要在界面上讲清楚：
 * - 关联：会议由仅本人可见变为事项成员可见，二次确认见 BindMeetingToMatterModal。
 * - 解除：会议退回仅本人可见，但曾经参加或同步过这场会议的同事仍然看得见——
 *   他们与这场会议的关系已经建立，解除关联不会清除这层关系。不说明这一点，
 *   律师会把「解除关联」误解为「收回会议」。
 */

import { useState } from "react";
import { Alert, Button, Modal, Space, Typography, message } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import { useMatterDetail, useUpdateMeeting } from "../../api/queries";
import type { Meeting } from "../../types/meeting";
import BindMeetingToMatterModal from "./BindMeetingToMatterModal";

const { Paragraph, Text } = Typography;

interface MeetingMatterBindingProps {
  meeting: Meeting;
}

export default function MeetingMatterBinding({ meeting }: MeetingMatterBindingProps) {
  const [bindOpen, setBindOpen] = useState(false);
  const [unbindOpen, setUnbindOpen] = useState(false);
  const [unbindError, setUnbindError] = useState<string | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const updateMut = useUpdateMeeting();
  const { data: matter } = useMatterDetail(meeting.matterId ?? "");
  const matterName = matter?.name ?? (meeting.matterId ? "已关联事项" : null);

  const handleUnbind = async () => {
    setUnbindError(null);
    try {
      await updateMut.mutateAsync({ id: meeting.id, data: { matterId: null } });
      messageApi.success("已解除与事项的关联");
      setUnbindOpen(false);
    } catch (err) {
      setUnbindError(
        err instanceof Error && err.message ? err.message : "保存失败，请稍后重试",
      );
    }
  };

  return (
    <div data-testid="meeting-matter-binding">
      {contextHolder}
      <Paragraph style={{ marginBottom: "var(--jerry-sp-1)" }}>
        <Text strong>关联事项：</Text>
        {meeting.matterId ? (
          <Space size="small">
            <Text>{matterName}</Text>
            <Button size="small" onClick={() => setBindOpen(true)}>
              更换事项
            </Button>
            <Button size="small" onClick={() => setUnbindOpen(true)}>
              解除关联
            </Button>
          </Space>
        ) : (
          <Space size="small">
            <Text type="secondary">未关联</Text>
            <Button size="small" icon={<LinkOutlined />} onClick={() => setBindOpen(true)}>
              关联到事项
            </Button>
          </Space>
        )}
      </Paragraph>
      <Text type="secondary" style={{ fontSize: "var(--jerry-font-size-base)" }}>
        {meeting.matterId
          ? "当前可见范围：该事项的全体在册成员都能查看这场会议的标题、时间、参会人、转录与摘要。"
          : "当前可见范围：仅您本人。关联到事项后，该事项的在册成员即可查看这场会议。"}
      </Text>

      {/* 关闭即卸载：下次打开是全新的选择流程，不残留上次的事项与检索词。 */}
      {bindOpen && (
        <BindMeetingToMatterModal
          open
          meetingId={meeting.id}
          currentMatterId={meeting.matterId}
          onClose={() => setBindOpen(false)}
          onBound={() => messageApi.success("已关联到事项")}
        />
      )}

      <Modal
        title="解除与事项的关联"
        open={unbindOpen}
        onCancel={() => {
          setUnbindOpen(false);
          setUnbindError(null);
        }}
        onOk={handleUnbind}
        okText={unbindError ? "重试" : "确认解除"}
        cancelText="取消"
        confirmLoading={updateMut.isPending}
        destroyOnHidden
      >
        <Paragraph>
          解除后，这场会议退回仅您本人可见，
          {matterName ? `「${matterName}」` : "该事项"}
          的成员不再从事项中看到它。
        </Paragraph>
        <Paragraph type="secondary" data-testid="unbind-relation-notice">
          但曾经参加或同步过这场会议的同事仍然能看到它——他们与这场会议的关系已经建立，
          解除关联不会清除这层关系。
        </Paragraph>
        {unbindError && (
          <Alert type="error" showIcon message="解除未生效" description={unbindError} />
        )}
      </Modal>
    </div>
  );
}
